import { Box, Button, IconButton, MenuItem, TextField, Typography } from "@mui/material";
import Table, { ColumnType } from "components/Table";
import useTitle from "hooks/useTitle";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { ResType } from "types";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { resetFilters } from "redux/reducers/taskboardSlice";

import { Visibility } from "@mui/icons-material";
import {
  getClientExcelproceedingFya,
  getExcelFyaSections,
} from "api/services/automation/income-tax-tan";
import { eProFyaCaseStaus, getAutFinancialYears } from "pages/atom-pro/constants";
import SearchContainer from "components/SearchContainer";
import Loader from "components/Loader";

import DateRange from "views/dashboard/OrgDashboard/DateRange";

const ExcelClientProceedingFya = (tab) => {
  const [active, setActive] = useState("FYA");
  useTitle("ATOM Pro | Excel Overview");
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const dispatch = useDispatch();
  const [section, setSection] = useState<any>(null);
  const [assessmentYear, setAssessmentYear] = useState<any>(null);
  const [sections, setSectionData] = useState<any>(null);
  const [caseStatus, setCaseStatus] = useState<any>(null);
  const params = useParams();
  const location = useLocation();
  const state: any = location.state;
  const [dates, setDates] = useState({ fromDate: null, toDate: null });

  useEffect(() => {
    return () => {
      dispatch(resetFilters());
    };
  }, []);

  const { data, isLoading }: ResType = useQuery(
    [
      "tan-e-proceedings-fya-excel",
      params.incometaxid,
      {
        search: search,
        limit: pageCount,
        offset: page * pageCount,
        section: section,
        assessmentYear: assessmentYear,
        caseStatus: caseStatus,
        ...dates,
      },
    ],
    getClientExcelproceedingFya,
    {
      onSuccess: (res) => {
        setTotalCount(res?.data?.count);
      },
    }
  );

  const { data: sectionData, isLoading: sectionIsloading }: ResType = useQuery(
    ["excel-e-proceedings-sections"],
    getExcelFyaSections,
    {
      onSuccess: (res) => {
        setSectionData(res?.data);
      },
    }
  );

  const onChange = (e: any) => {
    setSection(e.target.value);
    setPage(0);
  };

  const onStatusChange = (e: any) => {
    setCaseStatus(e.target.value);
    setPage(0);
  };

  const onAssessmentYearChange = (e: any) => {
    setAssessmentYear(e.target.value);
    setPage(0);
  };

  const handleSearch = (e) => {
    setSearch(e);
    setPage(0);
  };

  if (isLoading || sectionIsloading) return <Loader />;

  return (
    <>
      <Box m={1}>
        <Box mt={1}>
          <Box mb={1} display="flex" alignContent="center" mt={2}>
            <TextField
              name="assessmentYear"
              value={assessmentYear}
              onChange={onAssessmentYearChange}
              size="small"
              label="Assessment Year"
              select
              sx={{ width: "150px", marginRight: "20px" }}
            >
              <MenuItem key={""} value={""}>
                Select
              </MenuItem>
              <MenuItem key={""} value={"null"}>
                NA
              </MenuItem>
              {getAutFinancialYears().map((year: string) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              name="section"
              value={section}
              onChange={onChange}
              size="small"
              label="Section"
              select
              sx={{ width: "100px", marginRight: "20px" }}
            >
              <MenuItem key={""} value={""}>
                Select
              </MenuItem>
              <MenuItem key={""} value={"null"}>
                NA
              </MenuItem>
              {sections?.map((option: any, index: any) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              name="caseStatus"
              value={caseStatus}
              onChange={onStatusChange}
              size="small"
              label="Case Status"
              select
              sx={{ width: "100px", marginRight: "20px" }}
            >
              <MenuItem key={""} value={""}>
                Select
              </MenuItem>
              {eProFyaCaseStaus?.map((option: any, index: any) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>

            <SearchContainer
              debounced
              value={search}
              placeHolder="Search by Client | TAN | Proceeding Name"
              onChange={handleSearch}
            />
            <Box ml={1}>
              <DateRange dates={dates} setDates={setDates} setPage={setPage} />
            </Box>
            <Button
              sx={{ marginLeft: 2 }}
              onClick={() => {
                setSection("");
                setAssessmentYear(null);
                setSearch("");
                setDates({ fromDate: null, toDate: null });
              }}
              size="small"
              color="secondary"
              variant="outlined"
            >
              Clear Filters
            </Button>
          </Box>

          <Table
            sx={{ pb: 0, mb: 0, height: "455px" }}
            pagination={{ totalCount, pageCount, setPageCount, page, setPage }}
            data={data?.data.result || []}
            columns={columns}
            loading={isLoading}
            // onRowClick={handleRowClick}
          />
        </Box>
      </Box>
    </>
  );
};

const columns: Array<ColumnType> = [
  {
    key: "assesmentYear",
    title: "AY",
    render: (v) => <Typography width={"65px"}>{v?.ay === "0-01" ? "NA" : v?.ay}</Typography>,
  },
  {
    key: "",
    title: "Notice Sent Date",
    render: (v) => (
      <Typography sx={{ width: "90px" }}>{v?.noticeSentDate ? v.noticeSentDate : "--"}</Typography>
    ),
  },
  {
    key: "type",
    title: "Type",
  },
  {
    key: "proceedingName",
    title: (
      <Box display="flex" alignItems="center">
        Proceeding Name
      </Box>
    ),
    render: (v) => <Typography>{v?.proceedingName ? v?.proceedingName : "--"}</Typography>,
  },
  {
    key: "",
    title: "Section",
    render: (v) => <Typography>{v?.noticeSection ? v.noticeSection : "--"}</Typography>,
  },
  {
    key: "noticeDin",
    title: "Notice DIN",
    render: (v) => <Typography>{v?.noticeDin ? v?.noticeDin : "--"}</Typography>,
  },

  {
    key: "",
    title: "Compliance Date",
    render: (v) => <Typography>{v?.dateOfCompliance ? v.dateOfCompliance : "--"}</Typography>,
  },
  {
    key: "",
    title: "Response Submitted",
    render: (v) => (
      <Typography>{v?.dateResponseSubmitted ? v.dateResponseSubmitted : "--"}</Typography>
    ),
  },
  {
    key: "proceedingStatus",
    title: "Proceeding Status",
    render: (v) => <Typography>{v?.proceedingStatus ? v.proceedingStatus : "--"}</Typography>,
  },
  {
    key: "proceedingLimitationDate",
    title: "Limitation Date",
    render: (v) => (
      <Typography>{v?.proceedingLimitationDate ? v.proceedingLimitationDate : "--"}</Typography>
    ),
  },
  // {
  //   key: "",
  //   title: "Concluded Date",
  //   render: (v) => (
  //     <Typography
  //     // variant="subtitle2"
  //     // sx={{
  //     //   display: "inline-block",
  //     //   padding: "0px 5px",

  //     //   color: "#222222",
  //     //   fontSize: "12px",
  //     // }}
  //     >
  //       {v?.proceedingConcludedDate === "null" ? "--" : v.proceedingConcludedDate}
  //     </Typography>
  //   ),
  // },
  // {
  //   key: "",
  //   title: "Action",
  //   render: (rowData: any) => <Actions data={rowData} />,
  // }
];

function Actions(props: any) {
  const navigate = useNavigate();

  const handleViewMoreClick = () => {
    navigate(`/atom-pro/income-tax/proceeding-action/${props.data?.id}`);
  };

  return (
    <IconButton onClick={handleViewMoreClick} size="small">
      <Visibility />
    </IconButton>
  );
}

export default ExcelClientProceedingFya;
